import { auth } from '@/features/auth/providers/firebase';
import { paymentService } from '@/features/payments/services/payment-api';
import { useQuizStore } from '@/stores/quiz-store';
import { useSubscriptionStore } from '@/stores/subscription-store';
import { settlementQuiz } from '@/features/quiz/config/settlement-quiz';
import { turbopayApi } from '../api';
import { useClaimsStore } from '../store';
import type { FilingSubmission, SignedAuthorization } from '../types';

const FINALIZE_SETTLE_MS = 3000;

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function waitClaimsHydrated(): Promise<void> {
  if (useClaimsStore.persist.hasHydrated()) return Promise.resolve();
  return new Promise((resolve) => {
    const unsub = useClaimsStore.persist.onFinishHydration(() => {
      unsub();
      resolve();
    });
  });
}

export type PostAuthAction = 'retry' | 'resign' | 'relogin';

export class PostAuthError extends Error {
  readonly action: PostAuthAction;
  readonly code?: string;

  constructor(message: string, action: PostAuthAction, code?: string) {
    super(message);
    this.name = 'PostAuthError';
    this.action = action;
    this.code = code;
  }
}

export interface CompletePostAuthParams {
  uid: string;
  email: string;
  appleToken: string | null;
  purchaseStatus: string;
  purchaseSubId: string | null;
  purchasePlanId: string | null;
}

/** iOS FilingService.submitFiling: status completed and every submitted settlement stored. */
export function storedAuthorizationIds(
  submission: FilingSubmission,
  authorizations: SignedAuthorization[],
): Set<string> | null {
  if (submission.status !== 'completed') return null;
  const stored = new Set(
    (submission.items ?? [])
      .filter((item) => item.status === 'stored')
      .map((item) => item.settlement_id),
  );
  const submitted = new Set(authorizations.map((item) => item.settlement_id));
  if (stored.size !== submitted.size) return null;
  for (const id of submitted) {
    if (!stored.has(id)) return null;
  }
  return stored;
}

export function reopenSigningStep(): void {
  const index = settlementQuiz.steps.findIndex((step) => step.id === 'signing');
  const quiz = useQuizStore.getState();
  quiz.setCompleted(false);
  quiz.setStepIndex(index >= 0 ? index : settlementQuiz.steps.length - 1);
}

let inflightSync: Promise<boolean> | null = null;

/**
 * Bind locally signed PDFs to the authenticated user.
 * Matches iOS `synchronizePendingFilingsAction`: never throws; keeps drafts on failure.
 */
export function synchronizePendingFilings(token?: string): Promise<boolean> {
  if (inflightSync) return inflightSync;
  inflightSync = synchronizePendingFilingsOnce(token).finally(() => {
    inflightSync = null;
  });
  return inflightSync;
}

async function synchronizePendingFilingsOnce(token?: string): Promise<boolean> {
  await waitClaimsHydrated();
  const claims = useClaimsStore.getState();
  const pending = claims.signedAuthorizations;
  if (pending.length === 0) return true;

  try {
    const tokenID = token ?? (await auth.currentUser?.getIdToken());
    if (!tokenID) return false;

    const submission = await turbopayApi.submitFiling({
      token: tokenID,
      clientSubmissionId: claims.clientSubmissionId,
      profile: claims.profile,
      authorizations: pending,
    });
    const stored = storedAuthorizationIds(submission, pending);
    if (!stored) return false;
    useClaimsStore.getState().markAuthorizationsUploaded([...stored]);
    return true;
  } catch (err) {
    // Keep signed drafts locally. A confirmed session retries on the next launch.
    console.error('pending filing sync failed', err);
    return false;
  }
}

/**
 * Bind the no-user purchase to the Firebase user, then attach signed PDFs.
 * Filing failures do not block Get App — same as iOS after login.
 */
export async function completePostAuth(params: CompletePostAuthParams): Promise<void> {
  const {
    uid,
    email,
    appleToken,
    purchaseStatus,
    purchaseSubId,
    purchasePlanId,
  } = params;

  const purchaseOk = purchaseStatus === 'success';
  const claims = useClaimsStore.getState();
  const needsFiling = uid !== 'dev-user' && claims.signedAuthorizations.length > 0;
  const needsFinalize = uid !== 'dev-user' && purchaseOk && Boolean(purchaseSubId);

  if (uid === 'dev-user') {
    if (purchaseOk) useSubscriptionStore.getState().grantLocalAccess();
    return;
  }

  if (!needsFiling && !needsFinalize) return;

  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new PostAuthError('Your session expired. Please sign in again.', 'relogin');
  }

  if (needsFinalize && purchaseSubId) {
    try {
      await paymentService.finalizeSubscription({
        subscriptionId: purchaseSubId,
        email,
        ...(appleToken ? { appleToken } : { firebaseId: uid }),
        plan: purchasePlanId ?? undefined,
      });
      // Give the subscriptions backend time to index the bound uid/token.
      await wait(FINALIZE_SETTLE_MS);
    } catch (err) {
      console.error('finalize failed', err);
      throw new PostAuthError('We couldn\'t link your subscription. Please retry.', 'retry');
    }
  }

  if (needsFiling) {
    await synchronizePendingFilings(token);
  }

  if (purchaseOk) useSubscriptionStore.getState().grantLocalAccess();
}
